import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  Ship, 
  Package, 
  FileText, 
  ShieldCheck, 
  Handshake,
  ArrowRight,
  CheckCircle2,
  Star,
  Quote,
  ChevronRight,
  Cpu,
  Shirt,
  Cog,
  Apple,
  Building2,
  Car,
  FlaskConical,
  HeartPulse,
  Tractor,
  ShoppingBag
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

// Category icons
const categoryIcons = {
  "Electronics": Cpu,
  "Textiles": Shirt,
  "Machinery": Cog,
  "Food & Beverages": Apple,
  "Construction Materials": Building2,
  "Automotive Parts": Car,
  "Chemicals": FlaskConical,
  "Medical Supplies": HeartPulse,
  "Agriculture": Tractor,
  "Consumer Goods": ShoppingBag,
};

const Home = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const categories = [
    "Electronics", "Textiles", "Machinery", "Food & Beverages",
    "Construction Materials", "Automotive Parts", "Chemicals",
    "Medical Supplies", "Agriculture", "Consumer Goods"
  ];

  const steps = [
    { icon: FileText, titleKey: "howItWorks.step1.title", descKey: "howItWorks.step1.desc" },
    { icon: Package, titleKey: "howItWorks.step2.title", descKey: "howItWorks.step2.desc" },
    { icon: FileText, titleKey: "howItWorks.step3.title", descKey: "howItWorks.step3.desc" },
    { icon: ShieldCheck, titleKey: "howItWorks.step4.title", descKey: "howItWorks.step4.desc" },
    { icon: Handshake, titleKey: "howItWorks.step5.title", descKey: "howItWorks.step5.desc" },
  ];
  const aiAssistCards = [
    {
      title: "AI-assisted onboarding",
      points: [
        "Explains requirements in plain language",
        "Reduces incomplete supplier profiles",
      ],
    },
    {
      title: "Smart listings & RFQs",
      points: [
        "Helps generate clear product descriptions",
        "Improves RFQ quality and response rates",
      ],
    },
    {
      title: "Trust & verification support",
      points: [
        "Flags inconsistent information",
        "Assists human review and explains status",
      ],
    },
  ];

  const featuredSuppliers = [
    { id: 1, name: "Gulf Electronics Trading", location: "Dubai, UAE", categories: ["Electronics", "Consumer Goods"], verified: true },
    { id: 2, name: "Al Madina Textiles", location: "Abu Dhabi, UAE", categories: ["Textiles"], verified: true },
    { id: 3, name: "Emirates Industrial Supplies", location: "Sharjah, UAE", categories: ["Machinery", "Construction Materials"], verified: true },
    { id: 4, name: "Dubai Food Exports", location: "Dubai, UAE", categories: ["Food & Beverages"], verified: true },
  ];

  const testimonials = [
    { name: "John Mwangi", role: "CEO", company: "Kenya Imports Ltd", quote: "AfriGulf Market transformed how we source products from the UAE. The verified suppliers and secure payment system gave us confidence in cross-border transactions." },
    { name: "Ahmed Al-Hassan", role: "Export Manager", company: "Gulf Trading Co.", quote: "As a supplier, this platform opened up the entire East African market for us. The RFQ system is efficient and the escrow protection benefits both parties." },
    { name: "Sarah Okonkwo", role: "Procurement Head", company: "Kampala Enterprises", quote: "The logistics integration and transparent pricing make international procurement so much easier. Highly recommend for any African business looking to import from the Gulf." },
  ];

  const faqs = [
    { q: "How does the RFQ (Request for Quotation) process work?", a: "Buyers submit their requirements through our RFQ form specifying product category, quantity, and destination. Verified suppliers then submit competitive bids, and buyers can compare and select the best offer." },
    { q: "What is Trade Assurance and how does it protect me?", a: "Trade Assurance is our escrow-based payment protection. Funds are held securely until the buyer confirms delivery and satisfaction, protecting both parties in the transaction." },
    { q: "How are suppliers verified?", a: "All suppliers undergo a thorough verification process including business license verification, quality certifications, and operational history checks before receiving the verified badge." },
    { q: "What shipping routes do you support?", a: "We support direct shipping from UAE ports (Dubai, Abu Dhabi, Sharjah) to major East African destinations including Kenya, Uganda, Tanzania, Ethiopia, and Rwanda." },
    { q: "What is MOQ (Minimum Order Quantity)?", a: "MOQ (Minimum Order Quantity) is the smallest amount a supplier will sell. This varies by product and supplier, and is always clearly displayed on product listings." },
  ];

  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [aiChatInput, setAiChatInput] = useState("");
  const [aiMessages, setAiMessages] = useState([
    { role: "assistant", text: "Hi! I can help with onboarding, listings, and RFQs." },
  ]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${API_URL}/api/newsletter`, { email });
      toast.success(t('newsletter.success'));
      setEmail("");
    } catch (err) {
      toast.error("Failed to subscribe");
    }
    setSubscribing(false);
  };

  const handleAiSubmit = (e) => {
    e.preventDefault();
    const trimmed = aiChatInput.trim();
    if (!trimmed) return;
    setAiMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setAiChatInput("");
    setAiMessages((prev) => [
      ...prev,
      { role: "assistant", text: "Thanks! I can help with that. For now, try one of the example prompts below or be specific about your product, documents, or RFQ needs." },
    ]);
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1649519764734-85758bda0302?w=1920&q=80')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Content */}
        <div className="container-main relative z-10 py-20">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t('hero.headline')}
            </h1>
              <p className="text-lg sm:text-xl text-white/90 mb-10 max-w-2xl animate-fade-in stagger-1">
                {t('hero.subheadline')}
              </p>
              <p className="text-sm sm:text-base text-white/80 mb-8 max-w-2xl animate-fade-in stagger-2">
                Reducing onboarding friction, improving listing quality, and increasing cross-border trust.
              </p>
                <div className="flex flex-wrap gap-4 animate-fade-in stagger-3">
                  <Link to="/suppliers">
                    <Button className="btn-cta flex items-center gap-2" data-testid="hero-browse-btn" aria-label="Browse suppliers">
                      {t('hero.cta.browse')}
                      <ArrowRight className="w-5 h-5" aria-hidden="true" />
                    </Button>
                  </Link>
                  <Link to="/rfq">
                    <Button className="btn-secondary bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="hero-rfq-btn" aria-label="Request a quotation">
                      {t('hero.cta.rfq')}
                    </Button>
                  </Link>
                </div>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-1.5 text-sm text-white/90 backdrop-blur-sm animate-fade-in stagger-4">
                  <span aria-hidden="true">🤖</span>
                  <span>AI-assisted onboarding & listings</span>
                </div>
            </div>
          </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </section>

        {/* How It Works */}
        <section className="section-gap bg-white" data-testid="how-it-works-section">
          <div className="container-main">
          <div className="text-center mb-16">
            <span className="overline text-[#0D4F8B] mb-3 block">{t('howItWorks.overline')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('howItWorks.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="relative group">
                <Card className="card-base card-interactive p-6 text-center h-full hover-lift">
                  <CardContent className="p-0">
                    <div className="w-14 h-14 bg-[#0D4F8B]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-[#0D4F8B] transition-colors">
                      <step.icon className="w-7 h-7 text-[#0D4F8B] group-hover:text-white transition-colors" aria-hidden="true" />
                    </div>
                    <div className="w-8 h-8 bg-[#10B981] text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-sm">
                      {index + 1}
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{t(step.titleKey)}</h3>
                    <p className="text-sm text-slate-500">{t(step.descKey)}</p>
                  </CardContent>
                </Card>
                {index < 4 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-5 w-8 h-8 text-slate-300 -translate-y-1/2 z-10" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>
          </div>
        </section>
        
        {/* How AI Helps */}
        <section className="section-gap bg-[#F8FAFC]" data-testid="how-ai-helps-section">
          <div className="container-main">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                How AI assists trade workflows on AfriGulf Market
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aiAssistCards.map((card) => (
                <Card key={card.title} className="card-base p-6">
                  <CardContent className="p-0">
                    <h3 className="font-semibold text-slate-900 mb-4">{card.title}</h3>
                    <div className="space-y-2 text-sm text-slate-600">
                      {card.points.map((point) => (
                        <div key={point}>{point}</div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Trade Lanes Infographic */}
        <section className="py-20 bg-gradient-to-br from-[#0D4F8B] to-[#0A3D6B] text-white overflow-hidden">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="overline text-[#10B981] mb-3 block">UAE TO EAST AFRICA</span>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Direct Trade Routes
              </h2>
              <p className="text-white/80 mb-8">
                Established shipping lanes connecting Gulf ports with major East African destinations. 
                Reliable logistics partners ensure your goods arrive safely and on time.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {["Dubai → Mombasa", "Abu Dhabi → Dar es Salaam", "Sharjah → Kampala", "Jebel Ali → Addis Ababa"].map((route, i) => (
                  <div key={i} className="flex items-center gap-2 text-white/90">
                    <Ship className="w-5 h-5 text-[#10B981]" aria-hidden="true" />
                    <span className="text-sm">{route}</span>
                  </div>
                ))}
              </div>
              <Link to="/logistics" className="inline-block mt-8">
                <Button className="btn-cta" aria-label="Calculate shipping">
                  Calculate Shipping <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1583737077382-3f51318d6074?w=800&q=80"
                alt="Modern warehouse logistics"
                className="rounded-md shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white text-slate-900 p-4 rounded-md shadow-lg">
                <div className="text-3xl font-bold text-[#0D4F8B]">7-14</div>
                <div className="text-sm text-slate-500">Days Average Transit</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="section-gap" data-testid="categories-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="overline text-[#0D4F8B] mb-3 block">{t('categories.overline')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('categories.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
            {categories.map((category) => {
              const Icon = categoryIcons[category] || Package;
              return (
                <Link key={category} to={`/marketplace?category=${category}`}>
                  <Card className="card-base card-interactive p-6 text-center hover-lift">
                    <CardContent className="p-0">
                      <div className="w-12 h-12 bg-[#0D4F8B]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Icon className="w-6 h-6 text-[#0D4F8B]" aria-hidden="true" />
                      </div>
                      <h3 className="font-medium text-slate-900 text-sm">{category}</h3>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Suppliers */}
      <section className="section-gap bg-white" data-testid="featured-suppliers-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="overline text-[#0D4F8B] mb-3 block">{t('suppliers.featured.overline')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('suppliers.featured.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredSuppliers.map((supplier) => (
              <Card key={supplier.id} className="card-base card-interactive hover-lift">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-14 h-14 bg-[#0D4F8B] rounded-sm flex items-center justify-center text-white font-bold text-xl">
                      {supplier.name.charAt(0)}
                    </div>
                    {supplier.verified && (
                      <Badge className="bg-[#10B981] hover:bg-[#10B981]">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {t('suppliers.verified')}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3 text-xs text-slate-500">
                    <span>✔ Verified</span>
                    <span>🤖 AI-assisted profile</span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-1">{supplier.name}</h3>
                  <p className="text-sm text-slate-500 mb-3">{supplier.location}</p>
                  <div className="flex flex-wrap gap-1">
                    {supplier.categories.map((cat) => (
                      <Badge key={cat} variant="outline" className="text-xs">
                        {cat}
                      </Badge>
                    ))}
                  </div>
                  <Link to={`/suppliers/${supplier.id}`} className="block mt-4">
                    <Button variant="outline" className="w-full text-sm">
                      {t('suppliers.viewProfile')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/suppliers">
              <Button className="btn-primary">
                View All Suppliers <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-gap bg-[#F8FAFC]" data-testid="testimonials-section">
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="overline text-[#0D4F8B] mb-3 block">{t('testimonials.overline')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('testimonials.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="card-base p-8 relative">
                <CardContent className="p-0">
                  <Quote className="w-10 h-10 text-[#0D4F8B]/20 absolute top-6 right-6" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#0D4F8B] rounded-full flex items-center justify-center text-white font-semibold">
                      {testimonial.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-500">{testimonial.role}, {testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-gap bg-white" data-testid="faq-section">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <span className="overline text-[#0D4F8B] mb-3 block">{t('faq.overline')}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('faq.title')}
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`faq-${index}`} className="card-base px-6">
                  <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-[#0D4F8B]" data-testid="newsletter-section">
        <div className="container-main">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('newsletter.title')}
            </h2>
            <p className="text-white/80 mb-8">
              {t('newsletter.subtitle')}
            </p>
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder={t('newsletter.placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Newsletter email"
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20"
                  data-testid="newsletter-input"
                />
              <Button 
                type="submit" 
                className="btn-cta whitespace-nowrap"
                disabled={subscribing}
                data-testid="newsletter-submit"
              >
                {subscribing ? "..." : t('newsletter.cta')}
              </Button>
            </form>
          </div>
        </div>
      </section>
      
      {/* AI Assistant Floating Button + Chat */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          type="button"
          onClick={() => setIsAiChatOpen((open) => !open)}
          className="h-12 rounded-full bg-[#0D4F8B] text-white shadow-xl hover:shadow-2xl transition-shadow flex items-center gap-2 px-4"
          aria-label="Open AfriGulf AI Assistant (Beta)"
          title="AfriGulf AI Assistant (Beta)"
          aria-expanded={isAiChatOpen}
          aria-controls="afrigulf-ai-chat"
        >
          <span aria-hidden="true">🤖</span>
          <span className="text-sm font-semibold">AfriGulf AI Assistant (Beta)</span>
        </button>

        {isAiChatOpen && (
          <div
            id="afrigulf-ai-chat"
            className="absolute bottom-16 right-0 w-[320px] max-w-[90vw] rounded-xl border border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-label="AfriGulf AI Assistant chat"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <div className="text-sm font-semibold text-slate-900">AfriGulf AI Assistant</div>
                <div className="text-xs text-slate-500">Beta</div>
              </div>
              <button
                type="button"
                onClick={() => setIsAiChatOpen(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Close AI Assistant"
              >
                ✕
              </button>
            </div>
            <div className="p-4 space-y-3 text-sm text-slate-600 max-h-64 overflow-y-auto">
              {aiMessages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={`rounded-lg p-3 ${
                    message.role === "assistant" ? "bg-slate-100 text-slate-700" : "bg-[#0D4F8B]/10 text-slate-800"
                  }`}
                >
                  {message.text}
                </div>
              ))}
              <div className="text-xs text-slate-500">Try asking:</div>
              <div className="space-y-2 text-xs text-slate-600">
                <div>“Help me list my product”</div>
                <div>“What documents do suppliers need?”</div>
                <div>“Draft an RFQ for electronics”</div>
              </div>
            </div>
            <div className="px-4 pb-4">
              <form onSubmit={handleAiSubmit} className="flex items-center gap-2">
                <input
                  type="text"
                  value={aiChatInput}
                  onChange={(e) => setAiChatInput(e.target.value)}
                  placeholder="Type your question..."
                  className="flex-1 rounded-full border border-slate-200 px-3 py-2 text-xs text-slate-700 placeholder:text-slate-400"
                  aria-label="AI assistant message"
                />
                <button
                  type="submit"
                  className="rounded-full bg-[#0D4F8B] px-3 py-2 text-xs font-semibold text-white"
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;


